import { faTriangleExclamation } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Reveal } from "@progress/kendo-react-animation";
import {
  Card,
  CardHeader,
  ExpansionPanel,
  ExpansionPanelContent,
} from "@progress/kendo-react-layout";
import axios from "axios";
import { Component } from "react";
import {
  envVariables,
  userData,
  userDatasets,
  userEntries,
  userTemplates,
} from "../../Common/Store/CommonSignals";
import {
  entryValues,
  formErrors,
  galleryValues,
  selectedStep,
  selectedTab,
  tableValues,
  template,
} from "../../Entry/Manage/EditEntrySignals";
import { selectedTemplate } from "../../Entry/View/ViewEntrySignals";
import { selectedHamburgerIndex } from "../../Navigation/HamburgerMenu/HamburgerMenuSignals";

export class ErrorBoundary extends Component {
  static sentErrors = new Map();
  static EMAIL_COOLDOWN = 60000; // 1 minute cooldown per unique error

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, expanded: null };
    this.sendEmailDebounced = null;
  }

  generateErrorHash(error) {
    const stackLines = error.stack?.split("\n").slice(0, 3).join("") || "";
    const hashInput = `${error.message}${stackLines}`;

    // Hash using string reduction
    let hash = 0;
    for (let i = 0; i < hashInput.length; i++) {
      const char = hashInput.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  }

  shouldSendEmail(errorHash) {
    const now = Date.now();
    const lastSent = ErrorBoundary.sentErrors.get(errorHash);

    if (!lastSent) {
      return true;
    }

    // Check if cooldown period has passed
    const timeSinceLastSent = now - lastSent;
    return timeSinceLastSent > ErrorBoundary.EMAIL_COOLDOWN;
  }

  markErrorAsSent(errorHash) {
    ErrorBoundary.sentErrors.set(errorHash, Date.now());

    // Automatic cleanup of old errors (older than 1 hour)
    if (ErrorBoundary.sentErrors.size > 50) {
      const oneHourAgo = Date.now() - 3600000;
      for (const [hash, timestamp] of ErrorBoundary.sentErrors.entries()) {
        if (timestamp < oneHourAgo) {
          ErrorBoundary.sentErrors.delete(hash);
        }
      }
    }
  }

  componentDidCatch(error, errorInfo) {
    const formattedError = {
      id: 0,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    };

    const errorHash = this.generateErrorHash(formattedError);

    this.setState(
      {
        hasError: true,
        error: [formattedError],
      },
      () => {
        // Only send email if it wasn't sent recently
        if (this.shouldSendEmail(errorHash)) {
          this.sendEmail(formattedError);
          this.markErrorAsSent(errorHash);
        }
      }
    );
  }

  async sendEmail(error) {
    const url = envVariables.value.REACT_APP_AZ_LA_EMAIL;

    const emailBody = `
      <div style="font-family: Arial, sans-serif; color: #1a1a1a; line-height: 1.5;">
        <h2 style="color: red; margin-bottom: 16px;">Atlas Error Notification</h2>
        
        <p><strong>Message:</strong> ${error.message}</p>

        <p><strong>Stack Trace:</strong></p>
        <pre style="padding: 10px; border: 1px solid #ccc; white-space: pre-wrap; border-radius: 4px;">
      ${error.stack}
        </pre>

        <p><strong>Component Stack:</strong></p>
        <pre style="padding: 10px; border: 1px solid #ccc; white-space: pre-wrap; border-radius: 4px;">
      ${error.componentStack}
        </pre>

        <hr style="margin: 20px 0;" />

        <p style="margin-top: 20px;">
          Please investigate the above error.<br />
          This email was sent automatically by the Atlas application.
        </p>
      </div>
    `;

    const userTemplatesSignal = userTemplates.value;
    const userDatasetsSignal = userDatasets.value;
    const userEntriesSignal = userEntries.value;

    const generalSignals = {
      userData: userData.value,
      selectedHamburgerIndex: selectedHamburgerIndex.value,
      template: template.value,
      selectedTemplate: selectedTemplate.value,
      entryValues: entryValues.value,
      formErrors: formErrors.value,
      selectedTab: selectedTab.value,
      selectedStep: selectedStep.value,
      galleryValues: galleryValues.value,
      tableValues: tableValues.value,
    };

    const toBase64 = (obj) => {
      if (!obj) return null;

      try {
        const str = JSON.stringify(obj, null, 2);
        const bytes = new TextEncoder().encode(str);
        const binString = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
        return btoa(binString);
      } catch (e) {
        console.warn("Failed to encode object to Base64:", e);
        return null;
      }
    };

    const rawAttachments = [
      {
        Name: "userTemplatesSignal.json",
        ContentBytes: toBase64(userTemplatesSignal),
      },
      {
        Name: "userDatasetsSignal.json",
        ContentBytes: toBase64(userDatasetsSignal),
      },
      {
        Name: "userEntriesSignal.json",
        ContentBytes: toBase64(userEntriesSignal),
      },
      {
        Name: "generalSignals.json",
        ContentBytes: toBase64(generalSignals),
      },
    ];

    const attachments = rawAttachments.filter(
      (att) => att.ContentBytes && att.ContentBytes.trim() !== ""
    );

    const emailProperties = {
      To: "bdf75922.compassminerals0.onmicrosoft.com@amer.teams.ms",
      Subject: "ATLAS ERROR NOTIFICATION",
      Body: emailBody,
      Attachments: attachments,
    };

    try {
      await axios.post(url, emailProperties);
    } catch (e) {
      console.error("Error sending email:", e.message);
    }
  }

  setExpanded(id) {
    this.setState({
      expanded: id,
    });
  }

  componentWillUnmount() {
    if (this.sendEmailDebounced) {
      clearTimeout(this.sendEmailDebounced);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="k-d-flex !k-h-screen">
          <div className="left-side">
            <Card className="k-m-10 k-w-3/4 k-max-h-2/3">
              <CardHeader className="k-d-flex k-flex-col k-justify-content-center k-align-items-center">
                <div className="k-d-flex k-align-items-center">
                  <div className="k-mr-2">
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      size="3x"
                      style={{ color: "red" }}
                    />
                  </div>
                  <h2>Atlas has encountered an Error</h2>
                  <div>
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      size="3x"
                      style={{ color: "red" }}
                    />
                  </div>
                </div>
                <h4>Please report this if you believe this to be a bug</h4>
              </CardHeader>
              {this.state.error.map((error) => (
                <ExpansionPanel
                  key={error.id}
                  title={error.message}
                  subtitle="Click to view error details"
                  expanded={this.state.expanded === error.id}
                  onAction={(event) => {
                    this.setExpanded(event.expanded ? null : error.id);
                  }}
                >
                  <ExpansionPanelContent className="k-overflow-y-auto">
                    {this.state.expanded === error.id && (
                      <Reveal>
                        <pre>{error.stack}</pre>
                        <pre>{error.componentStack}</pre>
                      </Reveal>
                    )}
                  </ExpansionPanelContent>
                </ExpansionPanel>
              ))}
            </Card>
          </div>
          <div className="right-side"></div>
        </div>
      );
    }
    return this.props.children;
  }
}
